import { FC, useEffect, useState } from "react";
import cn from "classnames";

import styles from "./Paginate.module.scss";
import OldIcon from "../../brand/Icon";
import { Icon as NewIcon, IconName } from "capitalrx-components-2.0/lib/general";

import { PaginateProps } from "./Paginate.props";

// Try to use new Icon component with new icon names, fall back to old if not available
type IconComponent = typeof NewIcon | typeof OldIcon;
interface IconConfig {
  component: IconComponent;
  start: IconName | string;
  left: IconName | string;
  right: IconName | string;
  end: IconName | string;
  useNew: boolean;
}

const getIconConfig = (): IconConfig => {
  try {
    // Prefer new Icon component with chevron icons
    return {
      component: NewIcon,
      start: "chevron-start" as IconName,
      left: "chevron-left" as IconName,
      right: "chevron-right" as IconName,
      end: "chevron-end" as IconName,
      useNew: true,
    };
  } catch {
    // Fall back to old icon with carat icons
    return {
      component: OldIcon,
      start: "carat-left-end",
      left: "carat-left",
      right: "carat-right",
      end: "carat-right-end",
      useNew: false,
    };
  }
};

const iconConfig = getIconConfig();

/**
 * @deprecated Use `import { Pager } from capitalrx-components-2.0/lib/general` instead.
 */
const Paginate: FC<PaginateProps> = (props: PaginateProps): JSX.Element => {
  const { queryOptions, handleUpdateRequest, testId, displayDescription = true, resultsMoreFlag } = props;
  const resultsTotalCount = props.resultsTotalCount;

  const [atBeginning, setAtBeginning] = useState(true);

  const [atEnd, setAtEnd] = useState(resultsTotalCount <= queryOptions.results_per_page * queryOptions.page_number ? true : false);

  const rowMax = Math.min(queryOptions.page_number * queryOptions.results_per_page, resultsTotalCount);
  const rowMin = queryOptions.page_number * queryOptions.results_per_page - queryOptions.results_per_page + 1;
  const pageMax = Math.ceil(resultsTotalCount / queryOptions.results_per_page);

  useEffect(() => {
    if (queryOptions.page_number <= 1) {
      setAtBeginning(() => true);
    }
    if (queryOptions.page_number > 1) {
      setAtBeginning(() => false);
    }
    if (queryOptions.page_number < pageMax) {
      setAtEnd(() => false);
    }

    if (queryOptions.page_number >= pageMax) {
      setAtEnd(() => true);
    }
  }, [resultsTotalCount, queryOptions]);

  const handleClick = (intent: string): void => {
    let config = {
      page_number: queryOptions.page_number,
      results_per_page: queryOptions.results_per_page,
      order_by: queryOptions.order_by,
    };

    if (intent === "beginning") {
      // << clicked
      config = Object.assign(config, { page_number: 1 });
    } else if (intent === "increment") {
      // > clicked
      if (queryOptions.page_number < pageMax) {
        config = Object.assign(config, { page_number: queryOptions.page_number + 1 });
      }
    } else if (intent === "decrement") {
      // < clicked
      if (queryOptions.page_number > 1) {
        config = Object.assign(config, { page_number: queryOptions.page_number - 1 });
      }
    } else if (intent === "end") {
      // >> clicked
      config = Object.assign(config, {
        page_number: Math.ceil(resultsTotalCount / queryOptions.results_per_page),
      });
    }

    handleUpdateRequest(config);
  };

  const resultsMore = resultsMoreFlag ? ">" : "";
  let positionDescription = `${rowMin}-${rowMax} of ${resultsMore}${resultsTotalCount}`;
  if (!resultsTotalCount) {
    positionDescription = "No results found";
  }
  // backend returns maximum 1000 items
  if (resultsTotalCount === 1000) {
    positionDescription = `${rowMin}-${rowMax} of more than 1000 results`;
  }

  // this creates the position description, the (1-10 of 55) part
  const renderPositionDescription = () => {
    return (
      <div className={styles["positionDescription"]} data-testid={`test__count_${testId}`}>
        {positionDescription}
      </div>
    );
  };

  const atBeginningClass = cn({ [styles["disable"]]: atBeginning }, { [styles["enable"]]: !atBeginning });
  const atEndClass = cn({ [styles["disable"]]: atEnd }, { [styles["enable"]]: !atEnd });

  // this creates the << < > >> buttons / icons
  const renderCarats = () => {
    const Icon = iconConfig.component;
    const iconProps = iconConfig.useNew
      ? { size: "small" as const }
      : { id: "paginate-icon", className: "paginate-icon" };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const iconName = (name: string) => name as any;

    return (
      <div className={styles["buttons"]}>
        <div onClick={() => handleClick("beginning")} data-testid="test__paginate-icon-beginning" className={atBeginningClass}>
          <Icon {...iconProps} name={iconName(iconConfig.start)} />
        </div>
        <div onClick={() => handleClick("decrement")} data-testid="test__paginate-icon-decrease" className={atBeginningClass}>
          <Icon {...iconProps} name={iconName(iconConfig.left)} />
        </div>
        <div onClick={() => handleClick("increment")} data-testid="test__paginate-icon-increase" className={atEndClass}>
          <Icon {...iconProps} name={iconName(iconConfig.right)} />
        </div>
        <div onClick={() => handleClick("end")} data-testid="test__paginate-icon-end" className={atEndClass}>
          <Icon {...iconProps} name={iconName(iconConfig.end)} />
        </div>
      </div>
    );
  };

  return (
    <div className={styles["paginateClass"]} data-testid={testId ? `test__paginate-${testId}` : undefined}>
      <div className={styles["paginateUI"]}>
        {displayDescription ? renderPositionDescription() : null}
        {renderCarats()}
      </div>
    </div>
  );
};

/**
 * @deprecated
 */
export default Paginate;
